import {removeByIndex, replaceByIndex} from 'common/query-builder/cohorts-query-builder/util/index';
import {getClauseTrees, getLogicalOperators} from 'common/query-builder/cohorts-query-builder/util/expression-tree';

import {LogicalOperator} from '../enums/expression-enums';
import {OperatorNode} from '../expression/operator-node';
import type {FilterLabelPart, StagedPropertyFilterClause} from '../types/builder-state-types';
import type {ExpressionTree} from '../types/expression-types';
import type {Serializable} from '../types/serialized-cohort-types';
import type {Trackable} from '../types/tracking';
import {PropertyFilter} from './property-filter';
import type {ValidationArgs} from './staged-cohort';

export abstract class Clause implements Serializable, ExpressionTree, Trackable {
  public static getPropertyFiltersAndOperator(
    selectorTree: OperatorNode,
  ): {propertyFilters: PropertyFilter[]; propertyFilterOperator: LogicalOperator} {
    let propertyFilterOperator = LogicalOperator.And;
    let propertyFilters: PropertyFilter[] = [];
    if (selectorTree) {
      const propertyFilterTrees = getClauseTrees(selectorTree);
      propertyFilters = propertyFilterTrees.map((t) => PropertyFilter.createFromParts({tree: t}));
      if (propertyFilters.length > 1) {
        propertyFilterOperator = getLogicalOperators(selectorTree)[0];
      }
    }

    return {propertyFilters, propertyFilterOperator};
  }

  public readonly propertyFilterOperator = LogicalOperator.And;
  public readonly propertyFilters: PropertyFilter[] = [];

  constructor(options: any) {
    Object.assign(this, options);
  }

  public abstract getExpressionTree(): OperatorNode;
  public abstract isNegated(): boolean;
  public abstract setNegated(negated: boolean): Clause;
  public abstract toTrackingData(): Obj;
  public abstract isInvalidForDataGroup({dataGroupId, customEvents, cohorts}: ValidationArgs): boolean;
  public abstract serialize(): Obj;

  public isValid(): boolean {
    return true;
  }

  public getPropertyFilters() {
    return this.propertyFilters.filter((filter) => !!filter.property);
  }

  public getStagedFilterClause(propertyFilterIdx: number | null): StagedPropertyFilterClause {
    return this.propertyFilters[propertyFilterIdx]?.getStagedFilterClause() || null;
  }

  public getPropertyFilterProperty(propertyFilterIdx: number): string {
    const property = this.propertyFilters[propertyFilterIdx]?.property || ``;
    return Array.isArray(property) ? property.join(`.`) : property;
  }

  public getLabel(propertyFilterIdx: number): string {
    return this.propertyFilters[propertyFilterIdx]?.getLabel() || ``;
  }

  public getFilterLabelParts(propertyFilterIdx: number): FilterLabelPart[] {
    return this.propertyFilters[propertyFilterIdx]?.getFilterLabelParts() || [];
  }

  public removePropertyFilter(propertyFilterIdx: number): Clause {
    return this.newClause({propertyFilters: removeByIndex(this.propertyFilters, propertyFilterIdx)});
  }

  public commitStagedFilterClause(propertyFilterIdx: number): Clause {
    const newPropertyFilter = this.propertyFilters[propertyFilterIdx].commitStagedFilterClause();
    return this.replacePropertyFilter(newPropertyFilter, propertyFilterIdx);
  }

  public togglePropertyFilterOperator(): Clause {
    const propertyFilterOperator =
      this.propertyFilterOperator === LogicalOperator.And ? LogicalOperator.Or : LogicalOperator.And;
    return this.newClause({propertyFilterOperator});
  }

  public setStagedFilterClause(options: Partial<StagedPropertyFilterClause>, propertyFilterIdx: number | null): Clause {
    const newPropertyFilter = this.propertyFilters[propertyFilterIdx].setStagedFilterClause(options);
    return this.replacePropertyFilter(newPropertyFilter, propertyFilterIdx);
  }

  protected replacePropertyFilter(newPropertyFilter: PropertyFilter, propertyFilterIdx: number): Clause {
    const propertyFilters = replaceByIndex(this.propertyFilters, propertyFilterIdx, newPropertyFilter);
    return this.newClause({propertyFilters});
  }

  protected abstract newClause(options: any): Clause;
}
